import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { User, MapPin, Package, Building2, Calendar, Trash2, Briefcase, PenTool as Tool } from 'lucide-react';
import { getAgeColorClass } from '../../../utils/ageUtils';
import { useLanguage } from '../../../contexts/LanguageContext';
import { BedAssignment } from '../../../types/accommodation';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../../lib/firebase/config';
import { calculateAge } from '../../../utils/dateUtils';

type Props = {
  assignments: BedAssignment[];
  bedIndex: number;
  hotelName: string;
  hotelType: 'mainMakkah' | 'shiftingMakkah' | 'madinah';
  onRemove?: (pilgrimId: string) => void;
  onAssignNew?: () => void;
  allRoomAssignments?: BedAssignment[];
  userRole: string;
  userId: string;
};

// Predefined color combinations for nusuk groups
const NUSUK_GROUP_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
  { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
  { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200' },
  { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200' },
  { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200' }
];

export const PilgrimCard = ({
  assignments,
  bedIndex,
  hotelName,
  hotelType,
  onRemove,
  onAssignNew,
  allRoomAssignments = [],
  userRole,
  userId
}: Props) => {
  const { translations, direction } = useLanguage();
  const [pilgrimData, setPilgrimData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  // Get all unique nusuk groups in the room and assign colors
  const [nusukGroupColors, setNusukGroupColors] = useState<Map<string, typeof NUSUK_GROUP_COLORS[0]>>(new Map());

  // Fetch pilgrim data for all assignments in the room
  useEffect(() => {
    let isMounted = true;

    const fetchPilgrimData = async () => {
      try {
        // Get all unique pilgrim IDs from all assignments
        const allPilgrimIds = new Set([
          ...assignments.map(a => a.pid),
          ...allRoomAssignments.map(a => a.pid)
        ]);

        if (allPilgrimIds.size === 0) {
          setLoading(false);
          return;
        }

        const pilgrimIds = Array.from(allPilgrimIds);
        const pilgrimsRef = collection(firestore, 'Pilgrims');
        
        // Process pilgrim IDs in batches of 30 (Firestore limit)
        const batches = [];
        for (let i = 0; i < pilgrimIds.length; i += 30) {
          const batch = pilgrimIds.slice(i, i + 30);
          const q = query(pilgrimsRef, where('__name__', 'in', batch));
          batches.push(getDocs(q));
        }

        const snapshots = await Promise.all(batches);
        const data: Record<string, any> = {};
        const nusukGroups = new Set<string>();

        snapshots.forEach(snapshot => {
          snapshot.docs.forEach(doc => {
            const pilgrimData = doc.data();
            data[doc.id] = pilgrimData;
            if (pilgrimData.nusuk_group) {
              nusukGroups.add(pilgrimData.nusuk_group);
            }
          });
        });

        // Assign consistent colors to nusuk groups
        const colorMap = new Map<string, typeof NUSUK_GROUP_COLORS[0]>();
        Array.from(nusukGroups).forEach((group, index) => {
          colorMap.set(group, NUSUK_GROUP_COLORS[index % NUSUK_GROUP_COLORS.length]);
        });

        if (isMounted) {
          setPilgrimData(data);
          setNusukGroupColors(colorMap);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching pilgrim data:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPilgrimData();

    return () => {
      isMounted = false;
    };
  }, [assignments, allRoomAssignments]);

  // Check if user can view full details of this assignment
  const canViewFullDetails = (assignment: BedAssignment): boolean => {
    // Staff members can always be viewed fully
    if (assignment.isStaff) return true;
    
    if (userRole === 'admin') return true;
    if (userRole === 'guide' && assignment.gid === userId) return true;
    if (userRole === 'organizer' && assignment.oid === userId) return true;
    return false;
  };

  // Mask sensitive information
  const maskText = (text: string) => '****';

  // Get category color class
  const getCategoryColorClass = (category: string) => {
    switch (category) {
      case 'B2C':
        return 'bg-green-100 text-green-700';
      case 'B2G':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  // Format date to display - handles both timestamp and string formats
  const formatDate = (dateInput: any): string => {
    // If it's already a string in DD/MM/YYYY format, parse it and format as DD/M
    if (typeof dateInput === 'string' && dateInput.includes('/')) {
      const [day, month] = dateInput.split('/').map(Number);
      // Return just day/month without leading zero on month
      return `${day}/${month}`;
    }
    
    // If it's a timestamp, convert to DD/M format
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date input:', dateInput);
        return 'Invalid Date';
      }
      // Format as day/month without leading zeros on month
      return `${date.getDate()}/${date.getMonth() + 1}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateInput);
      return 'Invalid Date';
    }
  };

  // Sort assignments by date
  const sortedAssignments = [...assignments].sort((a, b) => {
    // Convert both to timestamps for comparison
    const getTimestamp = (input: any): number => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string' && input.includes('/')) {
        const [day, month, year] = input.split('/').map(Number);
        return new Date(year, month - 1, day).getTime();
      }
      return new Date(input).getTime();
    };
    
    return getTimestamp(a.ci) - getTimestamp(b.ci);
  });

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {sortedAssignments.map((assignment, index) => {
        const pilgrim = pilgrimData[assignment.pid];
        const pilgrimAge = pilgrim?.Date_of_Birth ? calculateAge(pilgrim.Date_of_Birth) : assignment.age;
        const isStaff = assignment.isStaff;

        return (
          <React.Fragment key={`${assignment.pid}-${index}`}>
            <div className="bg-white rounded-lg shadow-sm p-4 space-y-3 relative">
              {/* Header with Pilgrim Info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isStaff ? (
                    // Icon and style for staff/employees
                    <div className="p-2 rounded-full bg-gray-100">
                      <Briefcase className="text-gray-500" size={20} />
                    </div>
                  ) : (
                    // Regular icon for pilgrims
                    <div className={`p-2 rounded-full ${
                      pilgrim?.Gender?.toLowerCase() === 'male' ? 'bg-blue-100' : 'bg-pink-100'
                    }`}>
                      <User className={
                        pilgrim?.Gender?.toLowerCase() === 'male' ? 'text-blue-500' : 'text-pink-500'
                      } size={20} />
                    </div>
                  )}
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {canViewFullDetails(assignment) ? assignment.pn : maskText(assignment.pn)}
                      {isStaff && (
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 flex items-center gap-1">
                          <Tool size={12} />
                          <span>{translations?.pilgrimCard?.staff || 'Staff'}</span>
                        </span>
                      )}
                    </div>
                    {pilgrim?.Residence_Country && !isStaff && (
                      <div className="text-sm text-gray-500">
                        <MapPin size={14} className="inline mr-1" />
                        {pilgrim.Residence_Country}
                      </div>
                    )}
                  </div>
                </div>
                {/* Category Badge */}
                <div className="flex items-center gap-2">
                  {pilgrim?.pilgrim_category && !isStaff && (
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getCategoryColorClass(pilgrim.pilgrim_category)}`}>
                      {pilgrim.pilgrim_category}
                    </span>
                  )}
                  {/* Age Badge */}
                  {pilgrimAge && (
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs text-white
                    ${getAgeColorClass(pilgrimAge)}`}>
                      {pilgrimAge}
                    </span>
                  )}
                </div>
              </div>

              {/* Package and Nusuk Info - Only for pilgrims, not staff */}
              {!isStaff && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Package size={16} className="text-primary-500" />
                    <span className="font-medium">{pilgrim?.package_nickName}</span>
                  </div>
                  {pilgrim?.nusuk_group && (
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        nusukGroupColors.get(pilgrim.nusuk_group)?.bg || 'bg-gray-100'
                      } ${
                        nusukGroupColors.get(pilgrim.nusuk_group)?.text || 'text-gray-700'
                      }`}>
                        {translations?.packageInfo?.nusukGroup || 'Nusuk Group'}: {pilgrim.nusuk_group}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Guide Info */}
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-gray-500" />
                <span className="text-sm text-gray-600">
                  {canViewFullDetails(assignment) ? (
  direction === 'rtl' ? (
    <>
      {assignment.on} ← {assignment.gn}
    </>
  ) : (
    <>
      {assignment.gn} → {assignment.on}
    </>
  )
) : (
  direction === 'rtl' ? (
    <>
      {maskText('Organizer Name')} ← {maskText('Guide Name')}
    </>
  ) : (
    <>
      {maskText('Guide Name')} → {maskText('Organizer Name')}
    </>
  )
)}

                </span>
              </div>

             {/* Dates */}
<div className="text-gray-600 text-sm font-medium flex items-center gap-1 mt-1">
  <Calendar size={11} />
 <span className="truncate text-sm font-semibold">
  {direction === 'rtl'
    ? `${formatDate(assignment.co)} ← ${formatDate(assignment.ci)}`
    : `${formatDate(assignment.ci)} → ${formatDate(assignment.co)}`}
</span>
</div>

              {/* Remove Button - Only show if user has permission */}
              {onRemove && canViewFullDetails(assignment) && (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => onRemove(assignment.pid)}
    className={`absolute bottom-4 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors ${
      direction === 'rtl' ? 'left-4' : 'right-4'
    }`}
  >
    <Trash2 size={20} />
  </motion.button>
)}

              {/* User's Pilgrim Indicator */}
              {((userRole === 'guide' && assignment.gid === userId) ||
                (userRole === 'organizer' && assignment.oid === userId)) && (
                <div 
                  className="absolute -top-1 -right-1 w-3 h-3 bg-primary-500 rounded-full border border-white shadow-lg"
                  style={{
                    animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
                    boxShadow: '0 0 0 0 rgba(0, 137, 123, 0.7)'
                  }}
                >
                  <style>
                    {`
                      @keyframes ping {
                        75%, 100% {
                          transform: scale(2);
                          opacity: 0;
                        }
                      }
                    `}
                  </style>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}

      {/* Assign New Button */}
      {onAssignNew && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onAssignNew}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          <span>{translations?.pilgrimCard?.assignNew || 'Assign New Pilgrim'}</span>
        </motion.button>
      )}
    </motion.div>
  );
};